import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
  getArtworkForComplexLink,
} from "@/config/dashboard/artwork";
import { exhibitionColumnsVisibility } from "@/config/dashboard/exhibition";
import { generateTable } from "@/config/dashboard/general";
import { FormLayoutElement } from "@/config/forms/general";
import { Artwork, ArtworkInExhibition, Exhibition } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  ArtworkLinkGridCard,
  ExhibitionLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import ArtworkInExhibitionDrawer from "./drawer/ArtworkInExhibitionDrawer";
import { ExhibitionLinkDrawerSwitch } from "./drawer/ExhibitionLinkDrawerSwitch";

interface ComplexLinkProps {
  id: string;
  artwork: Artwork | undefined;
  exhibition: Exhibition | undefined;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

/**
 * ArtworkInExhibitions component
 * @returns {JSX.Element}
 */
const ArtworkInExhibitions = ({
  id,
  artwork,
  exhibition,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: complexeRelation } = useGetComplexRelationFields(
    id,
    "artworkInExhibitions",
    type,
  );

  const { mutate: deleteArtworkInExhibition } = useDeleteComplexLink(
    id,
    "artworkInExhibitions",
    type,
  );
  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteArtworkInExhibition({ id, relation: "artworkInExhibition" });
    },
    [deleteArtworkInExhibition],
  );

  const renderArtworkInExhibitionRowActions = useCallback(
    (data: ArtworkInExhibition) => {
      const preview =
            type === "exhibition"
              ? {
                  previewType: "artwork" as const,
                  previewId: data.artwork.id,
                  previewElements: [
                    {
                      label: t("oeuvresDescriptionTitre"),
                      value: data.artwork.oeuvresDescriptionTitre,
                      type: "text" as const,
                    },
                    {
                      label: t("oeuvresDescriptionNumeroEnregistrement"),
                      value: data.artwork.oeuvresDescriptionNumeroEnregistrement,
                      type: "text" as const,
                    },
                    {
                      label: t("oeuvresDescriptionResume"),
                      value: data.artwork.oeuvresDescriptionResume,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.artwork.attachments,
                      type: "image" as const,
                    },
                  ],
                }
              : type === "artwork"
                ? {
                    previewType: "exhibition" as const,
                    previewId: data.exhibition.id,
                    previewElements: [
                      {
                        label: t("attachments"),
                        value: data.exhibition?.attachments ?? [],
                        type: "image" as const,
                      },
                      {
                        label: t("expositionsDescriptionNumeroExpo"),
                        value: data.exhibition.expositionsDescriptionNumeroExpo,
                        type: "text" as const,
                      },
                      {
                        label: t("expositionsDescriptionResume"),
                        value: data.exhibition.expositionsDescriptionResume,
                        type: "richtext" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) =>
                type === "artwork" ? (
                  <ExhibitionLinkDrawerSwitch
                    open={open}
                    onOpenChange={onOpenChange}
                    parentId={id}
                    parentType="artwork"
                    queryId={id}
                    type={type}
                    fields={fields}
                    exhibition={data.exhibition}
                    artwork={data.artwork}
                    existingLink={data}
                    allLinks={complexeRelation}
                    complexRelationKey="artworkInExhibitions"
                  />
                ) : (
                  <ArtworkInExhibitionDrawer
                    fields={fields}
                    data={data}
                    queryId={id}
                    type={type}
                    open={open}
                    onOpenChange={onOpenChange}
                  />
                )
              }
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id, complexeRelation],
  );

  const columns: ColumnDef<ArtworkInExhibition, any>[] = useMemo(() => {
    const columnArtwork = getArtworkForComplexLink() as ColumnDef<
      ArtworkInExhibition,
      any
    >[];
    return [
      ...(type === "artwork"
        ? [
            {
              accessorKey: "expositionsDescriptionNumeroExpo",
              header: t("tab_expositionsDescriptionNumeroExpo"),
              enableResizing: true,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <TextCell
                    value={
                      row.original.exhibition.expositionsDescriptionNumeroExpo
                    }
                  />
                );
              },
            },
            {
              accessorKey: "artworkInExhbitionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                const attachments =
                  row.original.exhibition?.attachments ??
                  row.original.attachments ??
                  [];
                return (
                  <ImageCell
                    value=""
                    attachments={attachments}
                    domain="exhibition"
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionAnnee",
              header: t("tab_expositionsDescriptionAnnee"),
              size: 10,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionAnnee}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionTitre",
              header: t("tab_expositionsDescriptionTitre"),
              size: 300,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <HtmlCell
                    value={row.original.exhibition.expositionsDescriptionTitre}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionDates",
              header: t("tab_expositionsDescriptionDates"),
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionDates}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionLieu",
              header: t("tab_expositionsDescriptionLieu"),
              size: 300,
              cell: ({ row }: { row: Row<ArtworkInExhibition> }) => {
                return (
                  <div className="flex flex-col items-start text-wrap">
                    <p className="text-[14px] font-[500]">
                      {row.original.exhibition.expositionsDescriptionVille}
                    </p>
                    <p className="text-[14px] font-[400] text-gray-500">
                      {row.original.exhibition.expositionsDescriptionLieu}
                    </p>
                  </div>
                );
              },
            },
          ]
        : columnArtwork.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          }))),
      {
        accessorKey: "oeuvresDansExpositionsDescriptionNumeroCatalogue",
        header: t("tab_oeuvresDansExpositionsDescriptionNumeroCatalogue"),
      },
      createActionsColumnDef<ArtworkInExhibition>(({ row }) =>
        renderArtworkInExhibitionRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    const layoutBackLabel = t("relatedRecords");
    const searchInput = (
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("search")}
          className="w-80"
          value={searchContent}
          onChange={(e) => setSearchContent(e.target.value)}
        />
      </div>
    );
    if (type === "exhibition") {
      return (
        <ExhibitionToArtwork
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderArtworkInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={searchInput}
          listGridTitle={t("artworksRelated")}
          layoutBackLabel={layoutBackLabel}
          setIsAddMode={setIsAddMode}
        />
      );
    }
    return (
      <ArtworkToExhibition
        id={id}
        relatedData={complexeRelation}
        fields={fields}
        artwork={artwork}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderArtworkInExhibitionRowActions}
        searchContent={searchContent}
        searchInput={searchInput}
        listGridTitle={t("exhibitionsRelated")}
        layoutBackLabel={layoutBackLabel}
        setIsAddMode={setIsAddMode}
      />
    );
  }, [
    type,
    id,
    complexeRelation,
    fields,
    exhibition,
    isAddMode,
    columns,
    searchContent,
    setSearchContent,
    artwork,
    t,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

export default ArtworkInExhibitions;

type ExhibitionToArtworkProps = LinksTableProps<ArtworkInExhibition> & {
  exhibition: Exhibition | undefined;
};

// Oeuvres liées à une exposition
const ExhibitionToArtwork = ({
  id,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ExhibitionToArtworkProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "artwork-links-exhibitions-add";
  const STORAGE_KEY_LINKED = "artwork-links-exhibitions-linked";
  const defaultVisibilityAdd = {
    ...artworkColumnsVisibility,
    oeuvresDescriptionVignette: true,
    oeuvresPublicationStatus: false,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAdd, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAdd };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAdd, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAdd };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Artwork>({
    resultType: "artwork",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsArtworks: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      createActionsColumnDef<Artwork>(({ row }: { row: Row<Artwork> }) => {
          if (!exhibition) {
            return null;
          }
          return (
            <LinkDrawerRowActions
              previewType="artwork"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("oeuvresDescriptionTitre"),
                  value: row.original.oeuvresDescriptionTitre,
                  type: "text",
                },
                {
                  label: t("oeuvresDescriptionNumeroEnregistrement"),
                  value: row.original.oeuvresDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("oeuvresDescriptionResume"),
                  value: row.original.oeuvresDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ArtworkInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={exhibition.id}
                  type={"exhibition"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    artwork: row.original,
                    exhibition: exhibition,
                    oeuvresDansExpositionsDescriptionNumeroCatalogue: "",
                    oeuvresDansExpositionsNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [exhibition, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const tableOptionsAdd = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityAdd,
    onColumnOrderChange: setColumnsOrderAdd,
    state: {
      columnVisibility: columnVisibilityAdd,
      columnOrder: columnsOrderAdd,
    },
  };

  const tableOptionsLinked = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityLinked,
    onColumnOrderChange: setColumnsOrderLinked,
    state: {
      columnVisibility: columnVisibilityLinked,
      columnOrder: columnsOrderLinked,
    },
  };

  const addModeTable = (
    <DataTable
      columns={columnsArtworks}
      data={notAlreadyRelated?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelated?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={tableOptionsAdd}
    />
  );

  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={tableOptionsLinked}
    />
  );

  if (
    listGridTitle != null &&
    setIsAddMode != null &&
    searchInput != null
  ) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ArtworkLinkGridCard
                  artwork={row.artwork}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <ArtworkInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="exhibition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }

  return isAddMode ? addModeTable : listTable;
};

type ArtworkToExhibitionProps = LinksTableProps<ArtworkInExhibition> & {
  artwork: Artwork | undefined;
};

// Expositions liées à une oeuvre
const ArtworkToExhibition = ({
  id,
  relatedData,
  fields,
  artwork,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ArtworkToExhibitionProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_EXH = "exhibition-links-artwork-add";
  const STORAGE_KEY_LINKED_EXH = "exhibition-links-artwork-linked";
  const defaultVisibilityExh = { ...exhibitionColumnsVisibility, actions: true };
  const [columnVisibilityAddExh, setColumnVisibilityAddExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnVisibilityLinkedExh, setColumnVisibilityLinkedExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnsOrderAddExh, setColumnsOrderAddExh] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedExh, setColumnsOrderLinkedExh] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Exhibition>({
    resultType: "exhibition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("tab_expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("tab_expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("tab_expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("tab_expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("tab_expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Exhibition>(({ row }) => {
          if (!artwork) {
            return null;
          }
          return (
            <LinkDrawerRowActions
              previewType="exhibition"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
                {
                  label: t("expositionsDescriptionNumeroExpo"),
                  value: row.original.expositionsDescriptionNumeroExpo,
                  type: "text",
                },
                {
                  label: t("expositionsDescriptionResume"),
                  value: row.original.expositionsDescriptionResume,
                  type: "richtext",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ExhibitionLinkDrawerSwitch
                  key={row.original.id}
                  open={open}
                  onOpenChange={onOpenChange}
                  parentId={artwork.id}
                  parentType="artwork"
                  queryId={artwork.id}
                  type="artwork"
                  fields={fields}
                  exhibition={row.original}
                  artwork={artwork}
                  allLinks={relatedData}
                  complexRelationKey="artworkInExhibitions"
                />
              )}
            />
          );
        }),
    ];
  }, [artwork, fields, t, relatedData]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const tableOptionsAddExh = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityAddExh,
    onColumnOrderChange: setColumnsOrderAddExh,
    state: {
      columnVisibility: columnVisibilityAddExh,
      columnOrder: columnsOrderAddExh,
    },
  };

  const tableOptionsLinkedExh = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityLinkedExh,
    onColumnOrderChange: setColumnsOrderLinkedExh,
    state: {
      columnVisibility: columnVisibilityLinkedExh,
      columnOrder: columnsOrderLinkedExh,
    },
  };

  const addModeTable = (
    <DataTable
      columns={columnsExhibitions}
      data={notAlreadyRelated?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelated?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_EXH}
      options={tableOptionsAddExh}
    />
  );

  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_EXH}
      options={tableOptionsLinkedExh}
    />
  );

  if (
    listGridTitle != null &&
    setIsAddMode != null &&
    searchInput != null
  ) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_EXH}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ExhibitionLinkGridCard
                  item={row.exhibition}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <ExhibitionLinkDrawerSwitch
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                  parentId={id}
                  parentType="artwork"
                  queryId={id}
                  type="artwork"
                  fields={fields}
                  exhibition={row.exhibition}
                  artwork={row.artwork}
                  existingLink={row}
                  allLinks={relatedData}
                  complexRelationKey="artworkInExhibitions"
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }

  return isAddMode ? addModeTable : listTable;
};
